import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import {
  Calculator, Loader2, FileDown, IndianRupee, Building2, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtINR = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (v >= 10000000) return '₹' + (v / 10000000).toFixed(2) + ' Cr';
  if (v >= 100000) return '₹' + (v / 100000).toFixed(2) + ' L';
  return '₹' + v.toLocaleString('en-IN');
};

const CommercialsCalculatorDialog = ({ open, onClose, lead }) => {
  const { api } = useAuth();
  const [config, setConfig] = useState(null);
  const [tier, setTier] = useState('miniOutlet');
  const [cityType, setCityType] = useState('tier2');
  const [breakdown, setBreakdown] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/franchise-commercials/');
      setConfig(data);
    } catch (_e) { /* silent */ }
  }, [api]);

  useEffect(() => { if (open) { loadConfig(); setBreakdown(null); } }, [open, loadConfig]);

  const calculate = useCallback(async () => {
    if (!tier || !cityType) return;
    setCalculating(true);
    try {
      const { data } = await api.post('/franchise-commercials/calculate', { tier, cityType });
      setBreakdown(data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Calc failed';
      toast.error(typeof msg === 'string' ? msg : 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  }, [api, tier, cityType]);

  useEffect(() => { if (open && config) calculate(); }, [open, config, tier, cityType, calculate]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const response = await api.post('/franchise-commercials/quote-pdf', {
        tier, cityType,
        leadId: lead?.id,
        customerName: lead?.name,
        customerMobile: lead?.mobile,
        city: lead?.city,
      }, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dt = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `Servall_Quote_${tier}_${dt}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Quote PDF downloaded' + (lead ? ` and attached to ${lead.name}` : ''));
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="commercials-calculator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-emerald-600" /> Franchise Commercials Calculator
          </DialogTitle>
          <DialogDescription>
            Live ROI calculator. {lead && <span>Linked to <strong>{lead.name}</strong> ({lead.mobile})</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Franchise Tier *</Label>
              <Select value={tier} onValueChange={setTier}>
                <SelectTrigger data-testid="calc-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {config && Object.entries(config.tiers || {}).map(([k, t]) => (
                    <SelectItem key={k} value={k}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>City Type *</Label>
              <Select value={cityType} onValueChange={setCityType}>
                <SelectTrigger data-testid="calc-city-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tier1">Tier 1 (Metro) · {config?.cityMultipliers?.tier1}x</SelectItem>
                  <SelectItem value="tier2">Tier 2 · {config?.cityMultipliers?.tier2}x</SelectItem>
                  <SelectItem value="tier3">Tier 3 · {config?.cityMultipliers?.tier3}x</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {calculating && (
            <div className="py-8 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" /></div>
          )}

          {breakdown && !calculating && (
            <div className="space-y-3">
              {/* Tier description */}
              <Alert className="border-emerald-200 bg-emerald-50">
                <Building2 className="h-4 w-4 text-emerald-600" />
                <AlertDescription className="text-xs">
                  <strong className="text-emerald-800">{breakdown.tier.name}</strong>
                  {breakdown.tier.description && <> · {breakdown.tier.description}</>}
                </AlertDescription>
              </Alert>

              {/* Breakdown */}
              <div className="border rounded-lg overflow-hidden divide-y">
                <div className="flex justify-between p-3 hover:bg-muted/30">
                  <span>Brand Fee (one-time)</span>
                  <span className="font-mono font-medium">{fmtINR(breakdown.brandFee)}</span>
                </div>
                <div className="flex justify-between p-3 hover:bg-muted/30">
                  <div>
                    <div>Setup / Interior Cost</div>
                    <div className="text-xs text-muted-foreground">
                      Base {fmtINR(breakdown.setupCostBase)} × <Badge variant="outline" className="text-xs">{breakdown.cityMultiplier}x</Badge>
                    </div>
                  </div>
                  <span className="font-mono font-medium">{fmtINR(breakdown.setupCostAdjusted)}</span>
                </div>
                <div className="flex justify-between p-3 hover:bg-muted/30">
                  <div>
                    <div>Initial Bike Stock</div>
                    <div className="text-xs text-muted-foreground">
                      {breakdown.stockBikes} bikes × {fmtINR(breakdown.perBikeCost)}
                    </div>
                  </div>
                  <span className="font-mono font-medium">{fmtINR(breakdown.stockValue)}</span>
                </div>
                <div className="flex justify-between p-3 bg-amber-50 font-semibold">
                  <span className="flex items-center gap-1"><IndianRupee className="h-4 w-4" /> Total Setup Investment</span>
                  <span className="font-mono text-amber-900">{fmtINR(breakdown.totalInvestment)}</span>
                </div>
              </div>

              {/* ROI */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="border rounded-lg p-3 bg-sky-50 border-sky-200">
                  <div className="text-xs uppercase tracking-wider text-sky-700 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> Break-even bikes
                  </div>
                  <div className="text-2xl font-bold text-sky-900">{breakdown.breakEvenBikes ?? '—'}</div>
                  <div className="text-xs text-sky-700">@ {fmtINR(breakdown.perBikeMargin)}/bike margin</div>
                </div>
                <div className="border rounded-lg p-3 bg-purple-50 border-purple-200">
                  <div className="text-xs uppercase tracking-wider text-purple-700">Est. period</div>
                  <div className="text-2xl font-bold text-purple-900">{breakdown.breakEvenMonths ?? '—'} mo</div>
                  <div className="text-xs text-purple-700">at {breakdown.stockBikes} bikes/month</div>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={exportPdf} disabled={!breakdown || exporting} data-testid="export-quote-pdf-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
            Generate &amp; Share Quote (PDF)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CommercialsCalculatorDialog;
